import { Contract } from '../business/api/contracts/contract/contract';
import { Role } from '../business/api/contracts/contract/role.enum';
import { Universe } from '../business/api/contracts/contract/universe.enum';
import { ContractsBusinessModel } from '../business/api/contracts/contracts.business-model';
import { ContractFilters } from '../business/context/contract-filters';

export class ContractHelper {
    private static supportedContractTypes = {
        telco: {
            internet: {
                generic: Universe.INTERNET,
                internet: Universe.INTERNET,
                open: Universe.OPEN,
                '4ghome': Universe.QUATREG_HOME,
                homelive: Universe.HOMELIVE,
                airbox: Universe.AIRBOX,
                parnasse: Universe.PARNASSE,
                landline: Universe.INTERNET,
            },
            fixe: {
                generic: Universe.FIXE,
                landline: Universe.FIXE,
            },
            open: {
                open: Universe.OPEN,
                generic: Universe.OPEN,
            },
            mobilePostpaid: {
                '4ghome': Universe.QUATREG_HOME,
                homelive: Universe.HOMELIVE,
                generic: Universe.MOBILE,
                mobile: Universe.MOBILE,
                airbox: Universe.AIRBOX,
                open: Universe.OPEN,
                parnasse: Universe.PARNASSE,
            },
            mobilePrepaid: {
                '4ghome': Universe.QUATREG_HOME,
                homelive: Universe.HOMELIVE,
                generic: Universe.MOBILE,
                mobile: Universe.MOBILE,
                airbox: Universe.AIRBOX,
                open: Universe.OPEN,
            },
        },
        security: {
            generic: {
                generic: Universe.MAISON_PROTEGEE_V2,
                v1: Universe.MAISON_PROTEGEE,
            },
        },
    };

    /**
     * Is provided contract a Telco contract ?
     * @param {Contract} contract
     * @returns {boolean}
     */
    public static hasTelco(contract: Contract): boolean {
        return contract.telco !== undefined;
    }

    /**
     * Is provided contract a Security contract ?
     * @param {Contract} contract
     * @returns {boolean}
     */
    public static hasSecurity(contract: Contract): boolean {
        return contract.security !== undefined;
    }

    /**
     * Is VIM ELIGIBLE contract
     * @param {Contract} contract
     * @returns {boolean}
     */
    public static isVimEligibleContract(contract: Contract): boolean {
        return contract.telco && contract.telco.state && contract.telco.state.toUpperCase() === 'VIM_ELIGIBLE';
    }

    /**
     * Is FICTIF contract
     * @param {Contract} contract
     * @returns {boolean}
     */
    public static isFictifContract(contract: Contract): boolean {
        return (
            contract.role &&
            contract.role.toUpperCase() === Role.UTILISATEUR &&
            contract.telco &&
            contract.telco.marketType &&
            contract.telco.state &&
            contract.telco.marketType.toUpperCase() === 'RES' &&
            contract.telco.state.toUpperCase() === 'LOGGED'
        );
    }

    public static applyFilters(contracts: ContractsBusinessModel, filters: any): Contract[] {
        return contracts.contracts
            .map((contract) => ContractHelper.computeUniverse(contract))
            .filter((contract) => ContractHelper.isAuthorized(contract, filters));
    }

    /**
     * Then, it will compare the filters to the contract properties to return the authorization status.
     * @param {Contract} contract
     * @param {ContractFilters} filters
     * @return {boolean} authorizationsStatus
     */
    public static isAuthorized(contract: Contract, filters: ContractFilters): boolean {
        // Suggested contracts are not authorized.
        if (contract?.telco?.state?.toUpperCase() === 'SUGGESTED') {
            return false;
        }
        return Object.keys(filters).every((filterName) => {
            const filterParams = filters[filterName] || '';
            const filterValues = Array.isArray(filterParams) ? filterParams : [filterParams];
            switch (filterName) {
                case 'universe':
                case 'status':
                case 'brand':
                    return filterValues.some((value) => contract[filterName]?.toLowerCase() === value.toLowerCase());
                case 'marketType':
                    return filterValues.some(
                        (value) => contract.telco?.marketType?.toLowerCase() === value.toLowerCase(),
                    );
                default:
                    // Unknown filter, will not apply.
                    return true;
            }
        });
    }

    /**
     * Add universe param to Contract. Will not return anything.
     * @param {Contract} contract
     * @return contract with computed universe
     */
    public static computeUniverse(contract: Contract): Contract {
        const verticaleType = contract?.telco ? 'telco' : 'security';
        const type = contract.telco?.type ?? 'generic';
        let subType: string;
        if (verticaleType === 'telco') {
            subType = contract.telco?.subType ?? 'generic';
        } else {
            subType = contract.security?.version === 1 ? 'v1' : 'generic';
        }

        let universe: Universe;
        try {
            universe = ContractHelper.supportedContractTypes[verticaleType][type][subType];
        } catch (e) {
            // Contract `type` or `verticaleType` not recognized. Switching to Generic.
            universe = Universe.GENERIC;
        }
        // if the type/subType association is not found, switch to generic.
        return { ...contract, universe: universe || Universe.GENERIC };
    }

    public static isMobile(contract: Contract) {
        return contract.telco?.type.toLowerCase().startsWith('mobile');
    }

    public static isInternet(contract: Contract) {
        return contract.telco?.type.toLowerCase() === 'internet';
    }

    public static isOpen(contract: Contract) {
        return contract.telco?.type.toLowerCase() === 'open';
    }

    public static isLandline(contract: Contract) {
        return contract.telco?.type.toLowerCase() === 'fixe';
    }
}
